/** Ledger item 8: the receipt after a save — "Saved 1 Oct 10:44 Europe/London · audit
 *  #88123", links to the audit row, the change history and the deliveries of this save
 *  (all keyed by the audit uuid / event tag), and every follow-up with its outcome:
 *  done, queued (task link), failed (message), skipped. The editor stays open. A "… now"
 *  in view mode shows the same receipt where it was started. A save without an audit
 *  row of its own (`audit_uuid` null — a tour PATCH) shows no audit number and no
 *  audit-keyed links. */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Link, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { auditHref, changesHref, cond, consoleRouteFor, deliveryLogsHref } from '@/utils/gridLinks';
import { formatZoned } from '@/utils/zonedTime';

import { humanizeCode } from '../editor/format';
import type { RecordApplyResult, RecordFollowUp } from '../editor/types';

import { eventFires } from './model';

export interface LedgerReceiptData {
  result: RecordApplyResult;
  count: number;
  description: string;
}

const STATUS: Record<RecordFollowUp['status'], { icon: ReactNode; text: string }> = {
  done: { icon: <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />, text: 'done' },
  queued: { icon: <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />, text: 'queued' },
  failed: { icon: <ErrorOutlineIcon sx={{ fontSize: 14, color: 'error.main' }} />, text: 'failed' },
  skipped: { icon: <RemoveCircleOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />, text: 'skipped' },
};

const LIST_SX = { listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 } as const;

export function LedgerReceipt({
  receipt,
  timezone,
  eventStatusLabel,
  onClose,
  closeLabel = 'Close editor',
  deliveriesHref,
}: {
  receipt: LedgerReceiptData;
  timezone: string | null;
  eventStatusLabel?: (status: string) => string;
  onClose: () => void;
  /** The ledger closes the editor; a "… now" receipt closes itself. */
  closeLabel?: string;
  /** The record kind's Deliveries link (`config.deliveriesHref`); default: the save's
   *  `event_tag`. */
  deliveriesHref?: ((result: RecordApplyResult) => string | null) | undefined;
}) {
  const { result, count, description } = receipt;
  const statusLabel = eventStatusLabel ?? ((s: string) => humanizeCode(s).toLowerCase());
  const deliveries = deliveriesHref
    ? deliveriesHref(result)
    : result.event_tag
      ? deliveryLogsHref([cond('tag', result.event_tag)])
      : null;
  const links = [
    ...(result.audit_uuid
      ? [
          { label: 'Audit row', to: auditHref({ task_id: result.audit_uuid }) },
          { label: 'Change history', to: changesHref({ task_id: result.audit_uuid }) },
        ]
      : []),
    ...(deliveries ? [{ label: 'Deliveries', to: deliveries }] : []),
  ];

  return (
    <Box data-receipt sx={{ display: 'grid', gap: 1.25 }}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
        <CheckCircleOutlineIcon aria-hidden sx={{ fontSize: 20, color: 'success.main', mt: '1px' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} role="status">
            Saved {formatZoned(result.saved_ts, timezone, { weekday: false })}
            {result.audit_uuid && result.audit_id != null ? ` · audit #${result.audit_id}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {count} {count === 1 ? 'change' : 'changes'}
            {description ? ` · “${description}”` : ''}
          </Typography>
        </Box>
      </Box>

      {links.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, columnGap: 1.5 }}>
          {links.map((l) => (
            <Link key={l.label} component={RouterLink} to={l.to} variant="body2">
              {l.label}
            </Link>
          ))}
        </Box>
      )}

      {result.events.length > 0 && (
        <Box>
          <Typography variant="caption" component="h5" color="text.secondary" sx={{ fontWeight: 600, m: 0, mb: 0.5 }}>
            Events
          </Typography>
          <Box component="ul" sx={LIST_SX}>
            {result.events.map((e) => (
              <Box
                component="li"
                key={`${e.code}:${e.entity}:${e.id}`}
                sx={{ typography: 'caption', color: eventFires(e.status) ? 'text.primary' : 'text.secondary' }}
              >
                <Box component="code" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
                  {e.code}
                </Box>{' '}
                · {e.entity} #{e.id} · {statusLabel(e.status)}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {result.follow_ups.length > 0 && (
        <Box>
          <Typography variant="caption" component="h5" color="text.secondary" sx={{ fontWeight: 600, m: 0, mb: 0.5 }}>
            Follow-ups
          </Typography>
          <Box component="ul" sx={LIST_SX}>
            {result.follow_ups.map((f) => {
              const s = STATUS[f.status];
              const taskHref = f.task_id ? consoleRouteFor('backgroundtaskrecord', f.task_id) : null;
              return (
                <Box
                  component="li"
                  key={`${f.kind}:${f.code}`}
                  data-follow-up={f.status}
                  sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', typography: 'caption' }}
                >
                  <Box component="span" aria-hidden sx={{ display: 'inline-flex', mt: '2px' }}>
                    {s.icon}
                  </Box>
                  <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {f.label} — {s.text}
                    {f.status === 'queued' && taskHref && (
                      <>
                        {' · '}
                        <Link component={RouterLink} to={taskHref}>
                          task
                        </Link>
                      </>
                    )}
                    {f.message ? ` · ${f.message}` : ''}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {result.warnings.length > 0 && (
        <Box component="ul" sx={LIST_SX}>
          {result.warnings.map((w) => (
            <Box component="li" key={w} sx={{ display: 'flex', gap: 0.75, typography: 'caption' }}>
              <WarningAmberIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main', mt: '2px' }} />
              <span>{w}</span>
            </Box>
          ))}
        </Box>
      )}

      <Box>
        <Button size="small" variant="outlined" onClick={onClose}>
          {closeLabel}
        </Button>
      </Box>
    </Box>
  );
}
